"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";

/**
 * The GPA ↔ percentage converter's two linked calculators.
 *
 * The two lookup tables further down the page are NOT here: their values are constants
 * multiplied by a constant, so they are content, and they live in the content file as
 * `table` blocks. The line between the two halves of a tool page is "does this change
 * when the reader types", and these tables do not.
 */

const GPA_SCALE_FACTOR = 25; // percentage = 4.0-scale GPA × 25
const CGPA_SCALE_FACTOR = 9.5; // percentage = 10-point CGPA × 9.5 (CBSE convention)
const CGPA_MAX = 10;
/** 10 × 9.5: the percentage at which the CGPA reaches the top of its scale. */
const CGPA_CAP_PERCENT = CGPA_MAX * CGPA_SCALE_FACTOR;

/** English strings. A translated content file overrides these through `ui`. */
export const GPA_TO_PERCENTAGE_STRINGS = {
  scale4Heading: "4.0-Scale GPA ↔ Percentage",
  scale4Note: "Linear formula: percentage = GPA × 25. Enter either value.",
  gpa4Label: "GPA (0-4.0)",
  scale10Heading: "10-Point CGPA ↔ Percentage",
  scale10Note:
    "Common Indian formula (CBSE convention): percentage = CGPA × 9.5. Enter either value.",
  cgpa10Label: "CGPA (0-10)",
  percentLabel: "Percentage (0-100)",
  gpa4Stat: "GPA (4.0 scale)",
  cgpa10Stat: "CGPA (10-point)",
  percentStat: "Percentage",
  cgpaCapped:
    "Percentages above 95 show as a CGPA of 10, the top of the scale (95 ÷ 9.5 = 10).",
};

/**
 * Deliberately NOT locale-aware, and deliberately not named `formatNumber` — the
 * locale-aware one lives in `@/lib/format-number` and this is not a variant of it.
 *
 * Each half of this tool writes its answer into the OTHER half's
 * `<input type="number">`, and a number input holds exactly one syntax: ASCII digits and
 * an ASCII period. `3,40` in that box is not a differently-punctuated number, it is an
 * empty field. The two stat cards below echo those same boxes, so they read ASCII too
 * rather than disagreeing with the value they are quoting.
 */
function asciiFixed(value: number, decimals: number): string {
  if (!Number.isFinite(value)) {
    return "";
  }
  return value.toFixed(decimals);
}

function convertValue(
  raw: string,
  factor: number,
  max: number,
  decimals: number
): string {
  const n = parseNumberInput(raw);
  if (n === null || n < 0) {
    return "";
  }
  const result = n * factor;
  if (result < 0 || result > max) {
    return "";
  }
  return asciiFixed(result, decimals);
}

/**
 * Percentage to 10-point CGPA. CGPA × 9.5 only reaches 95%, so dividing 96–100 by 9.5
 * would give more than 10; those percentages are the top grade and show as the maximum
 * rather than a blank field. Below 0 or above 100 is not a percentage and stays blank.
 */
export function percentToCgpa(raw: string): string {
  const n = parseNumberInput(raw);
  if (n === null || n < 0 || n > 100) {
    return "";
  }
  return asciiFixed(Math.min(n / CGPA_SCALE_FACTOR, CGPA_MAX), 2);
}

/** True when a valid percentage is past the point where CGPA stops growing. */
export function isCgpaCapped(raw: string): boolean {
  const n = parseNumberInput(raw);
  return n !== null && n > CGPA_CAP_PERCENT && n <= 100;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

const NUMBER_INPUT_CLASS =
  "w-32 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

export default function GpaToPercentageConverterWidget() {
  const strings = useToolUi(GPA_TO_PERCENTAGE_STRINGS);
  // 4.0-scale converter state
  const [gpa4, setGpa4] = useState("");
  const [pct4, setPct4] = useState("");
  // 10-point CGPA converter state
  const [cgpa10, setCgpa10] = useState("");
  const [pct10, setPct10] = useState("");

  function handleGpa4(value: string) {
    setGpa4(value);
    setPct4(convertValue(value, GPA_SCALE_FACTOR, 100, 1));
  }

  function handlePct4(value: string) {
    setPct4(value);
    setGpa4(convertValue(value, 1 / GPA_SCALE_FACTOR, 4, 2));
  }

  function handleCgpa10(value: string) {
    setCgpa10(value);
    setPct10(convertValue(value, CGPA_SCALE_FACTOR, 100, 1));
  }

  function handlePct10(value: string) {
    setPct10(value);
    setCgpa10(percentToCgpa(value));
  }

  return (
    <>
      <section className="space-y-4 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.scale4Heading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.scale4Note}
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="gpa-4"
            >
              {strings.gpa4Label}
            </label>
            <input
              className={NUMBER_INPUT_CLASS}
              id="gpa-4"
              max={4}
              min={0}
              onChange={(e) => handleGpa4(e.target.value)}
              placeholder="3.40"
              step="0.01"
              type="number"
              value={gpa4}
            />
          </div>
          <span className="pb-2 text-muted-foreground">=</span>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="pct-4"
            >
              {strings.percentLabel}
            </label>
            <input
              className={NUMBER_INPUT_CLASS}
              id="pct-4"
              max={100}
              min={0}
              onChange={(e) => handlePct4(e.target.value)}
              placeholder="85"
              step="0.1"
              type="number"
              value={pct4}
            />
          </div>
        </div>
        {gpa4.length > 0 && pct4.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <StatCard label={strings.gpa4Stat} value={gpa4} />
            <StatCard label={strings.percentStat} value={`${pct4}%`} />
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.scale10Heading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.scale10Note}
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="cgpa-10"
            >
              {strings.cgpa10Label}
            </label>
            <input
              className={NUMBER_INPUT_CLASS}
              id="cgpa-10"
              max={10}
              min={0}
              onChange={(e) => handleCgpa10(e.target.value)}
              placeholder="8.2"
              step="0.01"
              type="number"
              value={cgpa10}
            />
          </div>
          <span className="pb-2 text-muted-foreground">=</span>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="pct-10"
            >
              {strings.percentLabel}
            </label>
            <input
              className={NUMBER_INPUT_CLASS}
              id="pct-10"
              max={100}
              min={0}
              onChange={(e) => handlePct10(e.target.value)}
              placeholder="77.9"
              step="0.1"
              type="number"
              value={pct10}
            />
          </div>
        </div>
        {cgpa10.length > 0 && pct10.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <StatCard label={strings.cgpa10Stat} value={cgpa10} />
            <StatCard label={strings.percentStat} value={`${pct10}%`} />
          </div>
        )}
        {isCgpaCapped(pct10) && (
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.cgpaCapped}
          </p>
        )}
      </section>
    </>
  );
}
